import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Loader2, Trash2, Send, PhoneCall, Mail, Phone,
  StickyNote, UserPlus, Check, ExternalLink, SlidersHorizontal, ShoppingBag, Undo2, CalendarDays,
  Home,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getNotesForIdentity,
  addContactNote,
  deleteContactNote,
} from '../../api/contact';
import { addPropertyBuyer } from '../../api/propertyManagement';
import { setBuyerPurchased, setBuyerRented } from '../../features/analytics/api/analyticsApi';
import RentedReturnPicker, { DEFAULT_RENTED_RETURN } from '../../features/contacts/components/RentedReturnPicker';
import { formatMelbourneShortDate } from '../../utils/melbourneTime';
import ReminderPresets from '../../features/contacts/components/ReminderPresets';
import BuyerPreferencesModal from '../../features/contacts/components/BuyerPreferencesModal';
import AgentNotesSummary from '../notes/AgentNotesSummary';

/**
 * Quick-actions popup for a buyer surfaced in a property's analytics breakdown.
 *
 * Lets the agent act on a buyer WITHOUT leaving the "Unique Buyers" list:
 *   - see their contact details (email / phone)
 *   - log a note about a call
 *   - set a "call again" reminder (surfaces in Who-to-Call)
 *   - send them an email (opens the agent's mail client)
 *   - attach them to this property's Buyers tab
 *   - file them under "Purchased" when they say they have already bought
 *   - file them under "Recently rented" when they are renting for now and will
 *     come back to buying later (CRM-2026-00132)
 *   - jump to their full profile if they want the deep view
 *
 * Opening the popup resolves (get-or-creates) the buyer to a CRM contact so notes,
 * reminders and the property link all attach to the same record.
 *
 * Props:
 *   buyer              { id, name, email, phone } — the row that was clicked
 *   propertyId         the property whose analytics list this is (for "Add to property")
 *   onClose            close handler
 *   onViewProfile(b)   optional — open the buyer's full contact profile page
 *   onAddedToProperty  optional — called after the buyer is attached to the property
 *   isPurchased        optional — seed state for the Purchased toggle
 *   onPurchasedChange(buyer, purchased) optional — the list behind the popup uses
 *                      this to drop (or restore) the row without a refetch
 *   onRentedChange(buyer, rented) optional — the same, for Recently rented
 */
export default function BuyerActionsModal({
  buyer,
  propertyId,
  onClose,
  onViewProfile,
  onAddedToProperty,
  onNoteChange,
  isPurchased = false,
  onPurchasedChange,
  onRentedChange,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [contactId, setContactId] = useState(null);
  const [contactName, setContactName] = useState(buyer?.name || '');
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [lastContacted, setLastContacted] = useState(null);
  // Most recent note added this session — its preset reminders link to it.
  const [lastNote, setLastNote] = useState(null);
  const [addingToProperty, setAddingToProperty] = useState(false);
  const [addedToProperty, setAddedToProperty] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  // "They've already bought" — CRM-2026-00049. Reversible from the same button.
  const [purchased, setPurchased] = useState(!!isPurchased);
  const [savingPurchased, setSavingPurchased] = useState(false);
  // Marking someone Recently Purchased opens a small prompt for the (optional)
  // date they bought, so an agent can set it right here instead of only on the
  // full profile (CRM-2026-00054 follow-up).
  const [showPurchasePrompt, setShowPurchasePrompt] = useState(false);
  const [purchaseDate, setPurchaseDate] = useState('');
  // "Renting for now, will buy later" (CRM-2026-00132). Out of the buyer lists
  // until rentedUntil, when they come back and the agent gets a call-back.
  const [rented, setRented] = useState(false);
  const [rentedUntil, setRentedUntil] = useState(null);
  const [savingRented, setSavingRented] = useState(false);
  const [showRentPrompt, setShowRentPrompt] = useState(false);
  const [rentOption, setRentOption] = useState(DEFAULT_RENTED_RETURN);
  const [rentDate, setRentDate] = useState('');

  const name = buyer?.name || buyer?.email || 'Buyer';
  const email = buyer?.email || '';
  const phone = buyer?.phone || '';
  const initial = name.charAt(0).toUpperCase();

  // contactId first: a match row that already knows its CRM contact (a Find
  // Buyers row, a database record with no email) must land on that exact
  // contact, not on whatever a name/email guess resolves to.
  const identity = {
    contactId: buyer?.contactId ?? buyer?.contact_id ?? null,
    buyerId: buyer?.id,
    email: buyer?.email,
    name: buyer?.name,
    phone: buyer?.phone,
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await getNotesForIdentity(identity);
      setContactId(res.data.contact_id);
      setContactName(res.data.contact_name || buyer?.name || '');
      setNotes(res.data.notes || []);
      setLastContacted(res.data.last_contacted || null);
      // The contact record is the source of truth for the Purchased toggle, so
      // the button always opens showing where this buyer actually stands.
      if (res.data.is_recently_purchased !== undefined) {
        setPurchased(!!res.data.is_recently_purchased);
      }
      setRented(res.data.buyer_status === 'rented');
      setRentedUntil(res.data.buyer_paused_until || null);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyer?.id, buyer?.email, buyer?.contactId, buyer?.contact_id]);

  useEffect(() => {
    if (buyer) load();
  }, [buyer, load]);

  // Re-seed when the popup is reopened on a different buyer.
  useEffect(() => { setPurchased(!!isPurchased); }, [isPurchased, buyer?.id, buyer?.contactId]);

  const handleAddNote = async () => {
    const text = newNote.trim();
    if (!text || !contactId) return;
    setSaving(true);
    try {
      const res = await addContactNote(contactId, { text });
      setNotes((prev) => [res.data, ...prev]);
      setLastNote(res.data);
      onNoteChange?.(res.data);
      setLastContacted({ agent_name: res.data.author_name || 'You', date: res.data.created_at, is_yours: true });
      setNewNote('');
      toast.success('Note added');
    } catch (e) {
      toast.error('Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!contactId) return;
    try {
      await deleteContactNote(contactId, noteId);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      toast.success('Note deleted');
    } catch (e) {
      toast.error('Failed to delete note');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleAddNote();
    }
  };

  // The newest note on this buyer when another agent wrote it, so this popup
  // shows the same note the list row behind it shows (CRM-2026-00050).
  const othersLastNote =
    lastContacted?.text && !notes.some((n) => n.id === lastContacted.note_id)
      ? lastContacted
      : null;

  // Move the buyer into the Purchased section (or back out of it). Flipping this
  // takes them out of Find Buyers, the property buyer match and the Buyers Match
  // audiences in one go, because all three build off the same candidate list.
  const applyPurchased = async (next, dateStr = '') => {
    if (savingPurchased) return;
    const contactRef = contactId ?? identity.contactId;
    if (!contactRef && !buyer?.id) {
      toast.error('Could not identify this buyer yet — try again in a moment.');
      return;
    }
    setSavingPurchased(true);
    try {
      await setBuyerPurchased({
        contactId: contactRef || undefined,
        buyerId: buyer?.id || undefined,
        purchased: next,
        // Only send a date when marking purchased; un-marking clears it server-side.
        ...(next ? { purchaseDate: dateStr || '' } : {}),
      });
      setPurchased(next);
      onPurchasedChange?.(buyer, next);
      setShowPurchasePrompt(false);
      if (!next) setPurchaseDate('');
      toast.success(
        next
          ? `${name} moved to Recently Purchased — they'll stop showing as a buyer`
          : `${name} is back in your buyer lists`
      );
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not update this buyer');
    } finally {
      setSavingPurchased(false);
    }
  };

  // The button either un-marks straight away, or opens the date prompt to mark.
  const handlePurchasedButton = () => {
    if (purchased) {
      applyPurchased(false);
    } else {
      setShowRentPrompt(false);
      setShowPurchasePrompt((v) => !v);
    }
  };

  const applyRented = async (next) => {
    if (savingRented) return;
    const contactRef = contactId ?? identity.contactId;
    if (!contactRef && !buyer?.id) {
      toast.error('Could not identify this buyer yet — try again in a moment.');
      return;
    }
    if (next && rentOption === 'date' && !rentDate) {
      toast.error('Pick the date they should come back to buying.');
      return;
    }
    setSavingRented(true);
    try {
      const data = await setBuyerRented({
        contactId: contactRef || undefined,
        buyerId: buyer?.id || undefined,
        rented: next,
        returnOption: rentOption,
        returnDate: rentDate,
      });
      setRented(next);
      setRentedUntil(next ? data?.status?.paused_until || null : null);
      onRentedChange?.(buyer, next);
      setShowRentPrompt(false);
      setRentOption(DEFAULT_RENTED_RETURN);
      setRentDate('');
      toast.success(
        next
          ? `${name} moved to Recently rented. You'll get a reminder to call them on ${formatMelbourneShortDate(data?.status?.paused_until)}`
          : `${name} is back in your buyer lists`
      );
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not update this buyer');
    } finally {
      setSavingRented(false);
    }
  };

  const handleRentedButton = () => {
    if (rented) {
      applyRented(false);
    } else {
      setShowPurchasePrompt(false);
      setShowRentPrompt((v) => !v);
    }
  };

  const handleAddToProperty = async () => {
    if (!propertyId || addingToProperty || addedToProperty) return;
    setAddingToProperty(true);
    try {
      const payload = contactId
        ? { contact_id: contactId }
        : { name, email, phone };
      await addPropertyBuyer(propertyId, payload);
      setAddedToProperty(true);
      toast.success(`${name} added to this property`);
      onAddedToProperty?.();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not add buyer to property');
    } finally {
      setAddingToProperty(false);
    }
  };

  if (!buyer) return null;

  return createPortal(
    <>
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-md flex-col rounded-2xl bg-white shadow-2xl max-h-[88vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — pinned so the close button stays reachable */}
        <div className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-gray-200 p-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#55C5D0] text-base font-bold text-white">
              {initial}
            </div>
            <div className="min-w-0">
              <h3 className="truncate text-sm font-bold text-gray-900">{contactName || name}</h3>
              {email && <p className="truncate text-xs text-gray-500">{email}</p>}
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Everything below the header scrolls as one region, so the composer
            and the reminder toggles can never clip off the bottom of the
            capped-height card on a short screen (CRM-2026-00054 follow-up). */}
        <div className="flex-1 min-h-0 overflow-y-auto">

        {/* Contact details */}
        <div className="space-y-1.5 border-b border-gray-100 px-4 py-3">
          {email ? (
            <a
              href={`mailto:${email}`}
              className="flex items-center gap-2 text-sm text-gray-600 transition-colors hover:text-[#3a99a3]"
            >
              <Mail className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
              <span className="truncate">{email}</span>
            </a>
          ) : (
            <p className="flex items-center gap-2 text-sm text-gray-400">
              <Mail className="h-3.5 w-3.5 flex-shrink-0" /> No email on file
            </p>
          )}
          {phone ? (
            <a
              href={`tel:${phone}`}
              className="flex items-center gap-2 text-sm text-gray-600 transition-colors hover:text-[#3a99a3]"
            >
              <Phone className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
              {phone}
            </a>
          ) : (
            <p className="flex items-center gap-2 text-sm text-gray-400">
              <Phone className="h-3.5 w-3.5 flex-shrink-0" /> No phone on file
            </p>
          )}
        </div>

        {/* Quick actions */}
        <div className="flex flex-wrap gap-2 border-b border-gray-100 px-4 py-3">
          <a
            href={email ? `mailto:${email}` : undefined}
            onClick={(e) => { if (!email) { e.preventDefault(); toast.error('No email on file for this buyer.'); } }}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${
              email
                ? 'bg-[#55C5D0] text-white hover:bg-[#47b1bb]'
                : 'cursor-not-allowed bg-gray-100 text-gray-400'
            }`}
          >
            <Send className="h-3.5 w-3.5" /> Send Email
          </a>
          {phone && (
            <a
              href={`tel:${phone}`}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-bold text-gray-700 transition-colors hover:border-[#55C5D0] hover:text-[#3a99a3]"
            >
              <PhoneCall className="h-3.5 w-3.5" /> Call
            </a>
          )}
          <button
            type="button"
            onClick={() => setPrefsOpen(true)}
            title="Edit this buyer's buying preferences"
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-bold text-gray-700 transition-colors hover:border-[#55C5D0] hover:text-[#3a99a3]"
          >
            <SlidersHorizontal className="h-3.5 w-3.5" /> Preferences
          </button>
          {propertyId && (
            <button
              type="button"
              onClick={handleAddToProperty}
              disabled={addingToProperty || addedToProperty}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors disabled:cursor-not-allowed ${
                addedToProperty
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'border border-[#55C5D0] text-[#3a99a3] hover:bg-[#55C5D0]/10'
              }`}
            >
              {addedToProperty ? (
                <><Check className="h-3.5 w-3.5" /> Added to property</>
              ) : addingToProperty ? (
                <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Adding…</>
              ) : (
                <><UserPlus className="h-3.5 w-3.5" /> Add to property</>
              )}
            </button>
          )}
          {onViewProfile && (
            <button
              type="button"
              onClick={() => onViewProfile(buyer)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-bold text-gray-700 transition-colors hover:border-gray-300"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Full profile
            </button>
          )}
          {/* "They've already bought and aren't looking any more" — the whole
              point of CRM-2026-00049. One click on the call, and they leave
              every buyer list for the Purchased section. Purchased and Recently
              rented are either/or, so only the one that is set shows while it
              is set; "Back to buyers" on it clears the way for the other. */}
          {!rented && (
            <button
              type="button"
              onClick={handlePurchasedButton}
              disabled={savingPurchased || loading}
              title={
                purchased
                  ? 'Put this buyer back in your active buyer lists'
                  : 'They have recently purchased and are no longer looking'
              }
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                purchased
                  ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                  : 'border border-gray-200 text-gray-700 hover:border-amber-400 hover:text-amber-700'
              }`}
            >
              {savingPurchased ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : purchased ? (
                <Undo2 className="h-3.5 w-3.5" />
              ) : (
                <ShoppingBag className="h-3.5 w-3.5" />
              )}
              {purchased ? 'Back to buyers' : 'Recently Purchased'}
            </button>
          )}
          {!purchased && (
            <button
              type="button"
              onClick={handleRentedButton}
              disabled={savingRented || loading}
              title={
                rented
                  ? 'Put this buyer back in your active buyer lists now'
                  : 'They are renting for now and will look to buy again later'
              }
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                rented
                  ? 'bg-sky-50 text-sky-700 hover:bg-sky-100'
                  : 'border border-gray-200 text-gray-700 hover:border-sky-400 hover:text-sky-700'
              }`}
            >
              {savingRented ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : rented ? (
                <Undo2 className="h-3.5 w-3.5" />
              ) : (
                <Home className="h-3.5 w-3.5" />
              )}
              {rented ? 'Back to buyers' : 'Recently Rented'}
            </button>
          )}
        </div>

        {/* Optional purchase-date prompt shown when marking someone Recently
            Purchased. Leaving it blank still files them; the date can be set or
            corrected later on their profile. */}
        {showPurchasePrompt && !purchased && (
          <div className="border-b border-amber-100 bg-amber-50/70 px-4 py-3">
            <label className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold text-amber-800">
              <CalendarDays className="h-3.5 w-3.5" /> Date of purchase (optional)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={purchaseDate}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setPurchaseDate(e.target.value)}
                className="rounded-lg border border-amber-200 bg-white px-2 py-1.5 text-xs text-gray-700 focus:border-amber-400 focus:outline-none"
              />
              <button
                type="button"
                onClick={() => applyPurchased(true, purchaseDate)}
                disabled={savingPurchased}
                className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {savingPurchased ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                Mark purchased
              </button>
              <button
                type="button"
                onClick={() => { setShowPurchasePrompt(false); setPurchaseDate(''); }}
                disabled={savingPurchased}
                className="rounded-lg px-2 py-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {showRentPrompt && !rented && (
          <div className="border-b border-sky-100 bg-sky-50/70 px-4 py-3">
            <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold text-sky-800">
              <CalendarDays className="h-3.5 w-3.5" /> When might they look to buy again?
            </p>
            <RentedReturnPicker
              option={rentOption}
              date={rentDate}
              onOptionChange={setRentOption}
              onDateChange={setRentDate}
            />
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={() => applyRented(true)}
                disabled={savingRented}
                className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {savingRented ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                Mark rented
              </button>
              <button
                type="button"
                onClick={() => { setShowRentPrompt(false); setRentDate(''); }}
                disabled={savingRented}
                className="rounded-lg px-2 py-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {rented && (
          <div className="border-b border-sky-100 bg-sky-50/70 px-4 py-2 text-[11px] font-semibold text-sky-800">
            Recently rented — hidden from Find Buyers, property matches and alerts
            {rentedUntil ? ` until ${formatMelbourneShortDate(rentedUntil)}, when you'll get a reminder to call them` : ''}.
          </div>
        )}

        {purchased && (
          <div className="border-b border-amber-100 bg-amber-50/70 px-4 py-2 text-[11px] font-semibold text-amber-800">
            Filed under Purchased — hidden from Find Buyers, property matches and
            buyer audiences until you put them back.
          </div>
        )}

        {/* Last contacted — visible to every agent (note text stays private) */}
        {lastContacted && (
          <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50 px-4 py-2 text-[11px] text-gray-500">
            <PhoneCall className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
            <span>
              Last contacted:{' '}
              <span className="font-semibold text-gray-700">
                {lastContacted.is_yours ? 'You' : lastContacted.agent_name}
              </span>
              {lastContacted.date && (
                <> · {new Date(lastContacted.date).toLocaleDateString()}</>
              )}
            </span>
          </div>
        )}

        {othersLastNote && (
          <div className="border-b border-amber-100 bg-amber-50 px-4 py-2.5">
            <p className="text-[10px] font-black uppercase tracking-widest text-amber-600">Last note</p>
            <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-amber-900">
              {othersLastNote.text}
            </p>
            <p className="mt-1 text-[11px] font-semibold text-amber-700/80">
              {othersLastNote.agent_name}
              {othersLastNote.date && ` · ${new Date(othersLastNote.date).toLocaleString('en-AU', {
                day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
              })}`}
            </p>
          </div>
        )}

        {/* Notes history — rendered ABOVE the composer so the agent sees their
            own previous notes immediately. It used to sit below the compose box
            AND the (tall) reminder presets, which pushed the history off the
            bottom of the capped-height modal so it read as "no notes"
            (CRM-2026-00056). Now it's the scrollable middle region, chat-style,
            with the composer pinned beneath it. */}
        <div className="flex min-h-[140px] flex-col border-b border-gray-100 p-4">
          <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            <StickyNote className="h-3.5 w-3.5 text-teal-500" /> Agent Notes
          </div>
          {/* AI summary of the notes shown here (Monday 13202770775) */}
          {!loading && !error && (
            <AgentNotesSummary
              notes={othersLastNote ? [...notes, othersLastNote] : notes}
              subject={contactName || name}
              className="mb-3"
            />
          )}
          {loading ? (
            <div className="flex h-24 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-teal-500" />
            </div>
          ) : error ? (
            <div className="py-6 text-center text-sm text-gray-500">
              Couldn't load notes.{' '}
              <button onClick={load} className="font-medium text-teal-600 hover:underline">
                Retry
              </button>
            </div>
          ) : notes.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-400">
              {othersLastNote ? 'No notes of your own yet.' : 'No notes yet.'}
            </p>
          ) : (
            <div className="space-y-2">
              {notes.map((note) => (
                <div
                  key={note.id}
                  className="group rounded-xl bg-gray-50 p-3 transition-colors hover:bg-gray-100/80"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-bold text-gray-900">{note.author_name || 'Agent'}</span>
                      <span className="text-gray-400">
                        {note.created_at ? new Date(note.created_at).toLocaleString() : ''}
                      </span>
                    </div>
                    <button
                      onClick={() => handleDeleteNote(note.id)}
                      className="p-1 text-gray-400 opacity-0 transition-all hover:text-red-500 group-hover:opacity-100"
                      title="Delete note"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
                    {note.text}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add note + reminder */}
        <div className="p-4">
          <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            <StickyNote className="h-3.5 w-3.5 text-amber-500" /> Log a call / note
          </div>
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="What did you discuss on the call?"
            rows={3}
            className="w-full resize-none rounded-lg border border-gray-200 p-2.5 text-sm focus:border-teal-400 focus:outline-none focus:ring-1 focus:ring-teal-400"
          />
          <div className="mt-2 flex items-center justify-between">
            <span className="text-[11px] text-gray-400">⌘/Ctrl + Enter to save</span>
            <button
              onClick={handleAddNote}
              disabled={saving || !newNote.trim() || loading || !contactId}
              className="inline-flex items-center gap-1.5 rounded-lg bg-teal-500 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-teal-600 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Save Note
            </button>
          </div>

          {/* Schedule a follow-up call — surfaces in the agent's Who-to-Call list */}
          {contactId && (
            <div className="mt-3">
              <ReminderPresets
                contactId={contactId}
                noteId={lastNote?.id || null}
                noteText={lastNote?.text || ''}
              />
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
    {prefsOpen && (
      // Wrapper lifts the shared modal (z-[60]) above this popup (z-[1100]).
      <div className="relative z-[1200]">
        <BuyerPreferencesModal
          identity={{ contactId, buyerId: buyer?.id, email, name: contactName || name, phone }}
          title={contactName || name}
          onClose={() => setPrefsOpen(false)}
        />
      </div>
    )}
    </>,
    document.body
  );
}
